import * as XLSX from 'xlsx';
import { TEMPLATE_COLUMNS, REQUIRED_COLUMNS, AUTO_COLUMNS, TemplateColumnName } from './studentTemplateGenerator';

export interface StudentRow {
  EnquiryId: string;
  StudentCode: string;
  BatchCode: string;
  UserName: string;
  Password: string;
  [key: string]: string;
}

export interface ValidationError {
  row: number;
  field: string;
  message: string;
}

export interface ParseResult {
  rows: StudentRow[];
  errors: ValidationError[];
  missingColumns: string[];
}

export function parseStudentExcel(buffer: ArrayBuffer): ParseResult {
  const wb = XLSX.read(buffer, { type: 'array' });
  const sheet = wb.Sheets[wb.SheetNames[0]];
  if (!sheet) return { rows: [], errors: [], missingColumns: [] };

  const jsonData = XLSX.utils.sheet_to_json<Record<string, any>>(sheet, { defval: '' });
  if (jsonData.length === 0) {
    return { rows: [], errors: [], missingColumns: [] };
  }

  const headerKeys = Object.keys(jsonData[0]);
  const normalizedHeaders = headerKeys.map(h => h.trim().toLowerCase());

  const missingColumns = REQUIRED_COLUMNS.filter(
    col => !normalizedHeaders.includes(col.toLowerCase())
  );

  const headerMap: Record<string, string> = {};
  headerKeys.forEach(key => {
    const match = TEMPLATE_COLUMNS.find(tc => tc.toLowerCase() === key.trim().toLowerCase());
    if (match) headerMap[match] = key;
  });

  const rows: StudentRow[] = jsonData.map(raw => {
    const row: any = {};
    TEMPLATE_COLUMNS.forEach(col => {
      const excelKey = headerMap[col];
      row[col] = excelKey ? String(raw[excelKey] ?? '').trim() : '';
    });
    return row as StudentRow;
  });

  const errors = validateStudentRows(rows);
  return { rows, errors, missingColumns };
}

export function validateStudentRows(rows: StudentRow[]): ValidationError[] {
  const errors: ValidationError[] = [];
  const seenStudentCodes = new Map<string, number>();

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];

    // Required field checks (AUTO is a valid value for AUTO_COLUMNS)
    for (const col of REQUIRED_COLUMNS) {
      const value = row[col];
      if (!value) {
        errors.push({ row: i, field: col, message: `${col} is required` });
      }
    }

    // EnquiryId must be a number (not AUTO)
    if (row.EnquiryId && row.EnquiryId.toUpperCase() !== 'AUTO') {
      const num = Number(row.EnquiryId);
      if (isNaN(num) || num <= 0 || !Number.isInteger(num)) {
        errors.push({ row: i, field: 'EnquiryId', message: 'EnquiryId must be a positive integer' });
      }
    }

    // Duplicate StudentCode detection (skip AUTO values)
    if (row.StudentCode && row.StudentCode.toUpperCase() !== 'AUTO') {
      const normalized = row.StudentCode.toUpperCase();
      if (seenStudentCodes.has(normalized)) {
        errors.push({
          row: i,
          field: 'StudentCode',
          message: `Duplicate StudentCode "${row.StudentCode}" (first seen at row ${seenStudentCodes.get(normalized)! + 2})`,
        });
      } else {
        seenStudentCodes.set(normalized, i);
      }
    }
  }

  return errors;
}

export function getRowErrors(errors: ValidationError[], rowIndex: number): ValidationError[] {
  return errors.filter(e => e.row === rowIndex);
}

export function rowHasErrors(errors: ValidationError[], rowIndex: number): boolean {
  return errors.some(e => e.row === rowIndex);
}
